import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Lock } from "lucide-react";

/**
 * Landing route for every notification click (`/notif-tap?d=<deep>&e=<event>`).
 *
 * SECURITY invariant — DO NOT WEAKEN:
 *  Every notification tap must go through PIN / password re-authentication
 *  before the intended screen is rendered, even if a live session already
 *  exists. This component is the single gatekeeper for that rule.
 *
 * Behaviour:
 *  - No session → send to /login and stash deep-link (login flow will
 *    consume `notif_deep` after auth).
 *  - Session + PIN enabled → force `lockNow()` so PinLockScreen appears;
 *    once the user unlocks, `AuthenticatedShell` navigates to the deep link.
 *  - Session + no PIN configured → send to /login so the user must
 *    re-authenticate with password before the deep link is opened.
 */
const DEEP_LINK_KEY = "lokdhara_notif_deep";
const DEEP_EVENT_KEY = "lokdhara_notif_event";

export default function NotifTapGate() {
    const [sp] = useSearchParams();
    const { user, logout } = useAuth();
    const deep = sp.get("d") || "/";
    const event = sp.get("e") || "info";

    useEffect(() => {
        try {
            sessionStorage.setItem(DEEP_LINK_KEY, deep);
            sessionStorage.setItem(DEEP_EVENT_KEY, event);
        } catch { /* private mode / storage full */ }

        // SECURITY: every notification tap forces a full re-authentication
        // cycle. Never trust the current session — even if the browser
        // has a live cookie / JWT, the user MUST re-enter their credentials
        // before any protected screen renders.
        (async () => {
            try { if (user) await logout(); } catch { /* ignore */ }
            // Clear any client-side token BEFORE the hard reload so the
            // login page cannot silently rehydrate the previous session.
            try {
                localStorage.removeItem("wmf_token");
                localStorage.removeItem("token");
            } catch { /* ignore */ }
            const target = deep.startsWith("/portal")
                ? "/customer-login?deep=" + encodeURIComponent(deep)
                : "/login?reauth=1&deep=" + encodeURIComponent(deep);
            // Use a full-page navigation to reset React state entirely.
            window.location.replace(target);
        })();
    }, [deep, event, user, logout]);

    return (
        <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="notif-tap-loading">
            <Card className="w-full max-w-sm p-6 rounded-2xl border-slate-100 text-center">
                <div className="mx-auto h-12 w-12 rounded-2xl bg-blue-900 grid place-items-center text-white">
                    <Lock className="h-5 w-5" />
                </div>
                <h2 className="mt-3 font-display text-lg font-bold text-slate-900">Verifying…</h2>
                <p className="mt-1 text-sm text-slate-500">
                    Please re-authenticate to open this notification.
                </p>
            </Card>
        </div>
    );
}

export function consumePendingDeepLink() {
    try {
        const d = sessionStorage.getItem(DEEP_LINK_KEY) || "";
        if (d) {
            sessionStorage.removeItem(DEEP_LINK_KEY);
            sessionStorage.removeItem(DEEP_EVENT_KEY);
        }
        return d;
    } catch {
        return "";
    }
}
